import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { createEntityStore } from '@redpanda/utils';
import { toast } from 'react-hot-toast';
import { getAccounts, deleteAccount as deleteAccountApi } from '../services/api';
import { storeMessages } from './storeMessages';

export type AccountType = 'ASSET' | 'LIABILITY' | 'EQUITY' | 'INCOME' | 'EXPENSE';
export const ACCOUNT_TYPES: AccountType[] = ['ASSET', 'LIABILITY', 'EQUITY', 'INCOME', 'EXPENSE'];

export interface Account {
  id: string;
  code: string;
  name: string;
  type: AccountType;
  description?: string | null;
  isActive?: boolean;
  balance?: number | string;
  _count?: { journalDetails: number };
}

/** 已有分錄使用的科目不能刪除、不能改類型（後端同規則：ACCOUNT_IN_USE、ACCOUNT_TYPE_LOCKED） */
export const isAccountUsed = (account: Account) => (account._count?.journalDetails ?? 0) > 0;

export const useAccountStoreBase = createEntityStore<Account>({
  entityName: 'account',
  fetchFn: (page: number) => getAccounts(page),
  deleteFn: deleteAccountApi,
  toast,
  messages: storeMessages,
});

/**
 * 科目下拉選單（分錄、銀行帳戶對話框共用）
 * stale-while-revalidate（R31，照 HR useEmployeeOptionsStore）：有快取先顯示，每次開對話框背景重抓，同時只送一個請求
 * 原本「快取永久有效」，分錄對話框又每次自己重抓一次
 */
interface AccountOptionsState {
  accounts: Account[];
  loaded: boolean;
  fetchAll: () => Promise<void>;
}

let pendingFetch: Promise<void> | null = null;

export const useAccountOptionsStore = create<AccountOptionsState>()(
  persist(
    (set) => ({
      accounts: [],
      loaded: false,
      fetchAll: () => {
        if (!pendingFetch) {
          pendingFetch = getAccounts(1, 1000)
            .then(response => set({ accounts: response.data.data ?? [], loaded: true }))
            .catch(error => console.error('Failed to load account options:', error))
            .finally(() => { pendingFetch = null; });
        }
        return pendingFetch;
      },
    }),
    { name: 'account-options', version: 1, partialize: state => ({ accounts: state.accounts, loaded: state.loaded }) }
  )
);
